import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw, Edit3, History } from 'lucide-react';
import { toast } from 'sonner';
import BalanceAdjustmentDialog from './BalanceAdjustmentDialog';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const BalanceManagement = ({ calendar, onBalanceChanged }) => {
  const [history, setHistory] = useState([]);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState('adjust'); // 'adjust' or 'reset'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [calendar]);

  const loadHistory = async () => {
    try {
      const response = await axios.get(
        `${API}/calendars/${calendar.id}/balance-history`,
        { withCredentials: true }
      );
      setHistory(response.data);
    } catch (error) {
      console.error('Error loading balance history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustClick = (type) => {
    setAdjustmentType(type);
    setShowAdjustDialog(true);
  };

  const handleAdjustmentComplete = () => {
    setShowAdjustDialog(false);
    loadHistory();
    onBalanceChanged();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Warning for joint calendars */}
      {calendar.type === 'joint' && calendar.shared_with_user_id && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">Calendrier partagé</p>
            <p className="text-amber-700">
              Les modifications de solde seront visibles par les deux utilisateurs et apparaîtront dans l'historique.
            </p>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-6 border-2 border-teal-200 hover:border-teal-300 transition-all">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center">
              <Edit3 className="h-6 w-6 text-teal-600" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-blue-900">Ajuster le solde</h3>
              <p className="text-sm text-gray-600">Sans supprimer l'historique</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Modifie simplement la valeur du solde. Les opérations enregistrées restent intactes.
          </p>
          <Button
            onClick={() => handleAdjustClick('adjust')}
            className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
            data-testid="adjust-balance-button"
          >
            Ajuster le solde
          </Button>
        </div>

        <div className="bg-white rounded-xl p-6 border-2 border-red-200 hover:border-red-300 transition-all">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-100 to-pink-100 flex items-center justify-center">
              <RefreshCw className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-blue-900">Réinitialiser</h3>
              <p className="text-sm text-gray-600">Efface tout l'historique</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Supprime toutes les opérations et repart à zéro. Cette action est irréversible.
          </p>
          <Button
            onClick={() => handleAdjustClick('reset')}
            variant="outline"
            className="w-full border-red-300 text-red-600 hover:bg-red-50"
            data-testid="reset-balance-button"
          >
            Réinitialiser le solde
          </Button>
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <History className="h-5 w-5 text-gray-600" />
            <h3 className="font-bold text-lg text-blue-900">Historique des modifications</h3>
          </div>
          <div className="space-y-3">
            {history.map((item) => (
              <div
                key={item.id}
                className={`p-4 rounded-lg border-2 ${
                  item.adjustment_type === 'reset'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-teal-50 border-teal-200'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-sm">
                        {item.adjustment_type === 'reset' ? '🔄 Réinitialisation' : '✏️ Ajustement'}
                      </span>
                      <span className="text-xs text-gray-500">
                        par {item.user_name}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600">De </span>
                      <span className="font-semibold">{item.old_balance.toFixed(2)} {calendar.currency}</span>
                      <span className="text-gray-600"> à </span>
                      <span className="font-semibold">{item.new_balance.toFixed(2)} {calendar.currency}</span>
                    </div>
                    {item.reason && (
                      <p className="text-xs text-gray-600 mt-1 italic">"{item.reason}"</p>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatDate(item.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <BalanceAdjustmentDialog
        open={showAdjustDialog}
        onClose={() => setShowAdjustDialog(false)}
        calendarId={calendar.id}
        adjustmentType={adjustmentType}
        onComplete={handleAdjustmentComplete}
      />
    </div>
  );
};

export default BalanceManagement;